'use strict'

import axios from 'axios'
import store from '@/store'
import {
  SESSION_HEADERS,
  SESSION_EXPIRES_TIME_VALUE,
  SESSION_EXPIRES_TIME_UNIT
} from 'utils/constants'
import moment from 'moment'
import { getSign } from '@didi/wsgsdk'
// modules
import commonService from './common'
import company from './company'
import costCenter from './costCenter'
import employee from './employee'
import financial from './financial'
import generic from './generic'
import project from './project'
import report from './report'
import user from './user'
import webCall from './webCall'
import accounts from './accounts'
import timePolicy from './timePolicy'
import creditPolicy from './creditPolicy'
import billing from './billing'
import users from './users'
import nfe from './nfe'
import coupon from './coupon'
import firstAccess from './firstAccess'
import companyGroup from './companyGroup'
import companyDataPurge from './companyDataPurge'
import incentives from './incentives'
import sapConcur from './sapConcur'
import { Omega, OmegaEventIds, OmegaBusiness, OmegaStatus } from 'common/omega'
export function addSign (config) {
  try {
    // query 参数
    let signParams = null
    // body参数
    let signBody = null

    // get query：业务方真实query参数
    signParams = config.params

    // post body：业务方真实body参数
    signBody = config.data

    const contentType = config.headers['Content-Type']

    const bodyIsString = typeof signBody === 'string'

    const funObj = {
      params: signParams,
      body: bodyIsString ? undefined : signBody,
      bodyString: bodyIsString ? signBody : undefined,
      contentType
    }

    if (contentType === 'application/json' && !bodyIsString) {
      funObj.bodySerializer = JSON.stringify
    }

    const sign = getSign(funObj)
    config.params = {
      ...config.params,
      ...{ wsgsig: sign }
    }
  } catch (e) {
    // 加签失败
    Omega.trackBusiness(OmegaEventIds.tech_b2bv3_other, {
      business: OmegaBusiness.addSign,
      type: OmegaStatus.error,
      status: OmegaStatus.op,
      msg: e.message,
      params: {
        url: config?.url,
        bodyIsString: typeof config?.data,
        contentType: config?.headers['Content-Type']
      }
    })
  }
}
function getHeaders (headers) {
  if (headers && headers['authorization'] && Object.keys(headers['authorization']).length) {
    localStorage.setItem(
      SESSION_HEADERS,
      JSON.stringify({ authorization: headers['authorization'] })
    )
    store.dispatch(
      'sessionExpiresTime/set',
      moment().add(SESSION_EXPIRES_TIME_VALUE, SESSION_EXPIRES_TIME_UNIT).format()
    )
  }
}
function setHeaders (config) {
  const session = JSON.parse(localStorage.getItem(SESSION_HEADERS))
  if (session) {
    config.headers.authorization = session.authorization
  }
}

export const loginPath = () => {
  return 'b2b-iam-app/authentication'
}

export const legacyV2 = () => {
  return process.env.NODE_ENV === 'production' ? 'b2b-legacy' : 'b2b-legacy-staging'
}

export const legacyV3 = () => {
  return process.env.NODE_ENV === 'production' ? 'b2b-legacy-v3' : 'b2b-legacy-v3-staging'
}

export const b2bReportKey = () => {
  return process.env.NODE_ENV === 'production'
    ? 'sW1KE3aDhQXapa65PrNNvCcb8bwNO8O7bihfxjJv'
    : 'zVJ3afOtJ1fz57TbXEnCIlX8unP74RYnB11JvMJN'
}

export const apiInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 40000,
  withCredentials: true
})
class DebounceTag {
  static requestMaps = {};
}
// API格式兼容性处理函数
/**
 * 检测响应是否为新格式
 * 新格式特征：包含 http_code 字段
 * @param {Object} responseData - 响应数据
 * @returns {boolean} 是否为新格式
 */
function isNewApiFormat (responseData) {
  return responseData && typeof responseData === 'object' && 'http_code' in responseData
}

/**
 * 将新格式转换为旧格式
 * 新格式: { "errno": 0, "errmsg": "成功", "data": {}, "http_code": 200 }
 * 旧格式: { "data": {}, "length": null, "errors": null }
 * @param {Object} newFormatData - 新格式数据
 * @returns {Object} 转换后的旧格式数据
 */
function convertNewToOldFormat (newFormatData) {
  const { data, errno, errmsg } = newFormatData

  // 如果errno不为0，表示有错误
  const hasError = errno !== 0

  return {
    data: data || {},
    length: data && Array.isArray(data) ? data.length : null,
    errors: hasError ? [errmsg || '未知错误'] : null,
    message: errmsg,
    errno,
    errmsg
  }
}

/**
 * 处理响应格式兼容性
 * @param {Object} response - axios响应对象
 * @returns {Object} 处理后的响应对象
 */
function handleApiFormatCompatibility (response) {
  const { data } = response

  // 检测是否为新格式
  if (isNewApiFormat(data)) {
    // 转换为旧格式
    const convertedData = convertNewToOldFormat(data)
    response.status = data.http_code
    // 返回转换后的响应
    const temp = {
      ...response,
      data: convertedData
    }
    // 设置http_code为实际的HTTP状态码
    if (data.http_code !== 200) {
      return Promise.reject(convertedData)
    }
    return temp
  }

  return response
}
apiInstance.interceptors.request.use(
  function (config) {
    if (config?.needSign) {
      addSign(config)
    }
    if (config.debounce) {
      // console.log('防抖请求')
      return new Promise((resolve, reject) => {
        const debounceId = config.debounceId
        const requestSymbol = `${config.method}${config.url}${new Date().getTime()}`
        if (DebounceTag.requestMaps[debounceId]) return
        // console.log('防抖请求，请求发出')
        DebounceTag.requestMaps[debounceId] = requestSymbol
        setHeaders(config)
        resolve(config)
      })
    }
    setHeaders(config)
    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

apiInstance.interceptors.response.use(
  function (response) {
    try {
      // B2B-V3对接多个后端服务，httpCode = 200的时候，errno的字段都不一致，返回格式不一致。
      // todo待后端梳理响应格式后再看能不能接埋点
    } catch (e) {
      console.log('omega upload error')
    }
    const { config } = response || {}
    if (config?.debounce) {
      // console.log('防抖响应')
      const debounceId = config.debounceId
      DebounceTag.requestMaps[debounceId] = undefined
    }
    // 修复token每次请求都不一致，每次请求都更新token过期时间
    getHeaders(response.headers)
    // 处理API格式兼容性转换
    const processedResponse = handleApiFormatCompatibility(response)

    return processedResponse
  },
  function (error) {
    try {
      /** 配合验签和挑战应答，上报非501和522的异常码 */
      const { response } = error || {} // code 和 message是在error.response不存在的时候使用，例如接口超时
      const { status, statusText, config } = response || {}
      if (response && (response?.status === 501 || response?.status === 522)) {
        Omega.trackBusiness(OmegaEventIds.tech_b2bv3_other, {
          business: OmegaBusiness.addSign,
          type: status,
          status: config?.needSign, // 区分是否是加签的接口
          msg: statusText,
          params: {
            code: config?.url,
            bodyIsString: typeof config?.data,
            contentType: config?.headers['Content-Type']
          }
        })
      } else {
        Omega.trackBusiness(OmegaEventIds.tech_b2bv3_other, {
          business: OmegaBusiness.addSign,
          type: status,
          status: config?.needSign, // 区分是否是加签的接口
          msg: statusText || error,
          params: {
            httpCode: status,
            code: config?.url,
            bodyIsString: typeof config?.data,
            contentType: config?.headers['Content-Type']
          }
        })
      }
    } catch (e) {
      console.log('omega upload error')
    }

    try {
      /** 异常统一埋点 */
      const { response, config, code, message } = error || {} // code 和 message是在error.response不存在的时候使用，例如接口超时
      if (config?.enableOmega) {
        const { status, data, headers } = response || {}
        const base = config?.baseURL
        const params = {
          path: config?.url,
          params: { data: config?.data, params: config?.params },
          status: status || code,
          code: data?.status,
          msg: data?.reasons?.join(',') || message,
          traceId: headers?.['didi-header-rid']
        }
        Omega.traceApiError(base, params)
      }
    } catch (e) {
      console.log('omega upload error')
    }
    // 不嵌入在iframe中，可以登出
    if (!(Boolean(error) && Boolean(error.response)) && !store.state.mainMenu.noMainMenuFromV4) {
      // error.response没有，即登出
      DebounceTag.requestMaps = {}
      console.log('interceptors logout', error)
      store.dispatch('session/logout')
      store.dispatch('company/clearSelectedCompany')
    }
    const { config } = error?.response || {}
    if (config?.debounce) {
      const requestId = config.debounceId
      console.log(requestId, '防抖响应Error')
      DebounceTag.requestMaps[requestId] = undefined
    }
    return Promise.reject(error)
  }
)

const apiService = {
  ...commonService,
  ...company,
  ...costCenter,
  ...employee,
  ...financial,
  ...generic,
  ...project,
  ...report,
  ...user,
  ...webCall,
  ...accounts,
  ...timePolicy,
  ...creditPolicy,
  ...billing,
  ...users,
  ...nfe,
  ...coupon,
  ...firstAccess,
  company: { ...company },
  companyGroup: { ...companyGroup },
  companyDataPurge: { ...companyDataPurge },
  billing: { ...billing },
  incentives: { ...incentives },
  sapConcur: { ...sapConcur },
  users: { ...users }
}

export default {
  install (Vue) {
    Vue.$apiService = apiService
    Vue.prototype.$apiService = apiService
  }
}
